import axios from "./axios";

export async function deleteCase(payload) {
  return axios
    .delete(`/cases/` + payload?.id)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function deleteCaseItem(payload) {
  return axios
    .delete(`/cases/${payload.case_id}/items/${payload.id}`)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function createCase(payload) {
  return axios
    .post(`/cases/`, { case: payload })
    .then((response) => wrapCaseWriteThen(response, "Request failed"))
    .catch((error) => wrapCaseWriteCatch(error, "Request failed"));
}

export async function createCaseItem(payload) {
  return axios
    .post(`/cases/${payload.case_id}/items`, { case_item: payload })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function updateCaseItem(caseId, itemId, payload) {
  return axios
    .put(`/cases/${caseId}/items/${itemId}`, { case_item: payload })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function postCaseSendEmailEmployee(payload) {
  return axios
    .post(`/cases/${payload.id}/send_email_employee`, payload)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function updateCase(payload) {
  return axios
    .put(`/cases/` + payload.id, { case: payload })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      // return Promise.reject(error.response?.data || error)
      if (error.status !== 200) return Promise.reject(error);
      else return Promise.reject(error.response?.data || error);
    });
}

/**
 * Load a single case. Pass `fields` to limit JSON (omit `items` / `last_item` and use fetchCaseItems).
 * @param {string|number} caseId
 * @param {{ fields?: string }} [options]
 */
export async function fetchCase(caseId, options = {}) {
  const params = {};
  if (options?.fields) params.fields = options.fields;

  return axios
    .get(`/cases/${caseId}`, { params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

/** Case header + form payload without embedded timeline items (items: GET /cases/:id/items). */
export const CASE_DETAIL_FIELDS_WITHOUT_ITEMS =
  "id,case_number,title,status,case_type,user_assigned,user_responsible,description,date,fields,customer,contact,project,report,service_agreement,order,note,priority,due_date,tag_definitions,transferred_to_case,transferred_from_cases,transferred_at,linked_emails,mail_thread,created_at,updated_at";

export async function transferCase({ id, target_case_id, note }) {
  return axios
    .post(`/cases/${id}/transfer`, { target_case_id, note })
    .then((response) => response.data)
    .catch((error) => {
      const data = error?.response?.data;
      return data && typeof data === "object"
        ? { ...data, __error: true, __status: error?.response?.status ?? 0 }
        : { errors: ["Request failed"], __error: true, __status: error?.response?.status ?? 0 };
    });
}

export async function fetchSiblingOpenCases(caseId) {
  if (!caseId) return { data: [], totalCount: 0 };
  return axios
    .get(`/cases/${caseId}/sibling_open_cases`)
    .then((response) => response.data)
    .catch(() => ({ data: [], totalCount: 0 }));
}

export async function fetchCaseItems(caseId, controller = {}) {
  const { page = 1, per_page = 25, sortBy = "created_at", sort = "desc" } = controller;
  const params = {
    page,
    per_page,
    sortBy,
    sort,
  };

  return axios({ method: "get", url: `/cases/${caseId}/items`, params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error?.response?.data || { data: [], totalCount: 0, currentCount: 0 };
    });
}

/**
 * Map an i18n language code to the API summary locale (Promang languages).
 * nb/no/nn → "no", en → "en", se/sv → "sv", pl → "pl"; default bokmål.
 */
function mapSummaryLocale(locale) {
  const value = String(locale || "no").toLowerCase();
  if (value.startsWith("en")) return "en";
  if (value.startsWith("sv") || value.startsWith("se")) return "sv";
  if (value.startsWith("pl")) return "pl";
  return "no";
}

/** Non-throwing error envelope for the (auto-updating) AI summary GET. */
function wrapCaseSummaryError(error) {
  const status = error?.response?.status;
  const raw = error?.response?.data;
  const base =
    raw && typeof raw === "object" ? raw : { errors: ["Request failed"] };
  return { ...base, __error: true, __status: status ?? 0 };
}

/**
 * Fetch the AI case summary. The summary is generated automatically by a backend
 * background job — there is NO user-facing refresh endpoint. Callers keep it fresh by
 * refetching when the feed changes and/or on a light interval while the view is open.
 *
 * @param {string|number} caseId
 * @param {string} [locale] current i18n language; mapped to API locale (no|en|sv|pl).
 * @returns {Promise<{
 *   ai_summary: string|null,
 *   ai_summary_updated_at: string|null,
 *   ai_summary_model: string|null,
 *   ai_summary_item_count: number,
 *   has_new_updates: boolean,
 *   generating: boolean,
 *   locale: string,
 * }>|Promise<{ __error: true, __status: number, errors: string[] }>}
 */
export async function fetchCaseSummary(caseId, locale) {
  if (!caseId) {
    return { __error: true, __status: 0, errors: ["Missing case id"] };
  }
  const params = { locale: mapSummaryLocale(locale) };

  return axios({ method: "get", url: `/cases/${caseId}/summary`, params })
    .then((response) => response.data)
    .catch((error) => wrapCaseSummaryError(error));
}

export async function fetchCasesStats() {
  return axios({ method: "get", url: "/cases/stats" })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchCases(controller) {
  let { filters, sort, sortBy, page, query, view, per_page, fields } =
    controller;

  const params = {
    query: query,
    page: page,
    per_page: per_page,
    sortBy: sortBy,
    sort: sort,
    filters: filters,
    view: view,
    fields: fields,
  };

  return axios({ method: "get", url: "/cases/", params: params })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function fetchCaseTypes() {
  return axios({ method: "get", url: "/cases/types" })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export async function createCaseFromCall(payload) {
  return axios
    .post(`/cases/create_from_call`, payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });
}

export const linkCallToCase = (payload) =>
  axios
    .post(`/cases/link_to_case`, payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });

export const processCallWithoutCase = (payload) =>
  axios
    .post(`/cases/process_without_case`, payload)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return Promise.reject(error.response?.data || error);
    });

/**
 * Search across customers, reports, projects, contacts for slash command (/63183).
 * Returns combined results with module type and displayTitle.
 *
 * Uses the lightweight /module_search endpoint (indexed ILIKE + pluck + hard
 * per-module LIMIT, no COUNT/serializers) instead of hitting the full index
 * endpoints (/customers/, /service/reports/, /projects/, /contacts/) once per
 * keystroke - those run pg_search fuzzy matching plus a COUNT(*) for
 * pagination and full serialization, which made module tagging feel very
 * slow, especially for projects.
 */
export async function searchSlashModules(query, customerId = null, signal = null) {
  if (!query || typeof query !== "string") return [];
  const q = query.trim();
  if (!q) return [];

  try {
    const res = await axios.get("/module_search", {
      params: { query: q, customer_id: customerId || undefined },
      signal,
    });
    const list = Array.isArray(res?.data?.data) ? res.data.data : [];
    return list.map((row) => ({
      id: row.id,
      module: row.module,
      labelKey: row.labelKey,
      displayTitle: row.displayTitle,
      object: { id: row.id, displayTitle: row.displayTitle },
    }));
  } catch (error) {
    if (axios.isCancel?.(error) || error?.name === "CanceledError") throw error;
    return [];
  }
}

function extractApiErrorMessages(payload, fallback) {
  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    return payload.errors.map((item) => String(item));
  }
  if (typeof payload?.errors === "string" && payload.errors.trim()) {
    return [payload.errors];
  }
  if (payload?.error) return [String(payload.error)];
  return [fallback];
}

function asObjectPayload(data) {
  return data && typeof data === "object" ? data : {};
}

/**
 * Session interceptor resolves HTTP errors as Axios responses. Treat status >= 400
 * as failure so send/link/thread callers do not toast success on a 500.
 */
function wrapMailAxiosThen(response, fallback) {
  const status = Number(response?.status) || 0;
  const data = response?.data;
  if (status >= 400) {
    const body = asObjectPayload(data);
    return {
      ...body,
      errors: extractApiErrorMessages(body, fallback),
      __error: true,
      __status: status,
    };
  }
  return data;
}

function wrapMailAxiosCatch(error, fallback) {
  const status = Number(error?.response?.status) || 0;
  const body = asObjectPayload(error?.response?.data);
  return {
    ...body,
    errors: extractApiErrorMessages(body, fallback),
    __error: true,
    __status: status,
  };
}

/**
 * Case write: session interceptor fulfills HTTP >=400. A persisted case (`id`) is
 * success even if a follow-up 500 body is what came back. Empty `errors: []` is
 * not a failure.
 */
export function caseWriteFailed(payload) {
  if (payload == null || payload === false) return true;
  if (typeof payload !== "object") return true;
  const id = payload.id ?? payload.data?.id;
  if (id) return false;
  if (payload.__error) return true;
  const errors = payload.errors;
  if (Array.isArray(errors)) return errors.length > 0;
  if (typeof errors === "string") return Boolean(errors.trim());
  if (errors && typeof errors === "object") return Object.keys(errors).length > 0;
  if (payload.error) return true;
  return false;
}

export function caseWriteErrorMessage(payload, fallback = "Request failed") {
  return extractApiErrorMessages(asObjectPayload(payload), fallback).join(", ");
}

function stripWriteErrorsIfPersisted(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;
  if (data.id == null && data.data?.id == null) return data;
  const rest = { ...data };
  delete rest.errors;
  delete rest.__error;
  delete rest.error;
  delete rest.__status;
  return rest;
}

function wrapCaseWriteThen(response, fallback) {
  const status = Number(response?.status) || 0;
  const body = asObjectPayload(response?.data);
  const id = body.id ?? body.data?.id;
  if (id) return stripWriteErrorsIfPersisted(response?.data);
  if (status >= 400) {
    return {
      ...body,
      errors: extractApiErrorMessages(body, fallback),
      __error: true,
      __status: status,
    };
  }
  return response?.data;
}

function wrapCaseWriteCatch(error, fallback) {
  const status = Number(error?.response?.status) || 0;
  const body = asObjectPayload(error?.response?.data);
  const id = body.id ?? body.data?.id;
  if (id) return stripWriteErrorsIfPersisted(error.response.data);
  return {
    ...body,
    errors: extractApiErrorMessages(body, fallback),
    __error: true,
    __status: status,
  };
}

export async function fetchCaseMailThread(caseId) {
  return axios
    .get(`/cases/${caseId}/mail_thread`)
    .then((response) => wrapMailAxiosThen(response, "Request failed"))
    .catch((error) => wrapMailAxiosCatch(error, "Request failed"));
}

export async function fetchCaseMailThreadCandidates(caseId) {
  return axios
    .get(`/cases/${caseId}/mail_thread_candidates`)
    .then((response) => wrapMailAxiosThen(response, "Request failed"))
    .catch((error) => wrapMailAxiosCatch(error, "Request failed"));
}

export async function postCaseLinkMailThread(caseId, payload) {
  return axios
    .post(`/cases/${caseId}/link_mail_thread`, payload)
    .then((response) => wrapMailAxiosThen(response, "Request failed"))
    .catch((error) => wrapMailAxiosCatch(error, "Request failed"));
}

export async function postCaseSendCustomerEmail(caseId, payload) {
  return axios
    .post(`/cases/${caseId}/send_customer_email`, payload)
    .then((response) => wrapMailAxiosThen(response, "Request failed"))
    .catch((error) => wrapMailAxiosCatch(error, "Request failed"));
}

/** Read-only reconciliation: this endpoint never calls Graph send. */
export async function fetchCaseCustomerEmailStatus(caseId, requestKey) {
  return axios.get(`/cases/${caseId}/customer_email_status`, {
    params: requestKey ? { request_key: requestKey } : {},
  }).then((response) => wrapMailAxiosThen(response, "Request failed"))
    .catch((error) => wrapMailAxiosCatch(error, "Request failed"));
}
